import { useState, useCallback } from "react";
import { URLS } from "../config/url";

export const useTokenManager = () => {
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    setTokenLoading(true);
    setTokenError(null);

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(`${URLS}/api/user/refreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || "Failed to refresh token");
      }

      const accessTokenExpires = Date.now() + 3 * 60 * 1000; // 3 minutes
      localStorage.setItem("accessToken", json.accessToken);
      localStorage.setItem("accessTokenExpires", accessTokenExpires.toString());
      if (json.refreshToken) {
        localStorage.setItem("refreshToken", json.refreshToken);
      }

      setTokenLoading(false);
      return json.accessToken;
    } catch (err) {
      setTokenError(err.message);
      setTokenLoading(false);
      throw err;
    }
  }, []); // Empty dependency array since it only uses localStorage and URLS

  // Function to get a valid access token
  const getValidAccessToken = useCallback(async () => {
    setTokenLoading(true);
    setTokenError(null);

    const accessToken = localStorage.getItem("accessToken");
    const expires = localStorage.getItem("accessTokenExpires");

    if (!accessToken || !expires) {
      throw new Error("No access token available");
    }

    if (Date.now() > parseInt(expires)) {
      return await refreshAccessToken();
    }

    setTokenLoading(false);
    return accessToken;
  }, [refreshAccessToken]); // Depends on refreshAccessToken

  return { getValidAccessToken, refreshAccessToken, tokenLoading, tokenError };
};
